/**
 * Pure helpers for the myfitvent (athlete) sign in and sign up screen.
 *
 * The screen itself lives in components/auth/myfitvent. Everything here is
 * free of React and the network so it can be unit tested: the code field's
 * sanitiser, the email check and its plain-words messages, the copy that
 * changes with the mode and the delivery channel, and the translation of the
 * participant auth API's error strings into words an athlete can act on.
 *
 * Athlete auth is our own email (or SMS) code flow, not Clerk. Sign in and
 * sign up call the same endpoints: verify-otp creates the account when there
 * is none, so the mode only changes the copy, never the request.
 */

export type MyFitventAuthMode = 'signin' | 'signup';
export type CodeChannel = 'email' | 'sms';
export type AuthErrorContext = 'email' | 'code' | 'phone';

export const CODE_LENGTH = 6;

/**
 * Keep digits only, capped at six. Runs on every change, so typing a letter
 * does nothing and a pasted "123 456" or "Your code is 123456." becomes
 * "123456". The input has no maxLength on purpose: the browser would cut a
 * pasted "123 456" to "123 45" before this ever saw it.
 */
export function sanitiseCode(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

export function isCompleteCode(code: string): boolean {
  return new RegExp(`^\\d{${CODE_LENGTH}}$`).test(code);
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmail(raw: string | null | undefined): boolean {
  return EMAIL_SHAPE.test((raw ?? '').trim());
}

/** The message under the email field, or null when the address will do. */
export function emailError(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (!value) return 'Enter your email address.';
  if (!EMAIL_SHAPE.test(value)) return "That doesn't look like an email address. Check for a typo.";
  return null;
}

/** Reads `?mode=` so other pages can link straight to sign up. */
export function parseAuthMode(param: string | null | undefined): MyFitventAuthMode {
  const value = (param ?? '').trim().toLowerCase();
  return value === 'signup' || value === 'sign-up' || value === 'join' ? 'signup' : 'signin';
}

/**
 * Where to go after signing in. Only same-site paths are honoured, so a
 * crafted `?redirect=https://elsewhere` (or the protocol-relative
 * `//elsewhere`) cannot bounce a freshly signed-in athlete off the site.
 */
export function safeRedirect(param: string | null | undefined, fallback = '/hub/home'): string {
  const value = (param ?? '').trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return fallback;
  return value;
}

export interface EmailCopy {
  title: string;
  intro: string;
}

export function emailStepCopy(mode: MyFitventAuthMode, brandName = 'myfitvent'): EmailCopy {
  if (mode === 'signup') {
    return {
      title: 'Every race you enter, in one place.',
      intro:
        'Your tickets, start times and results for every event, with any organiser. Free, and no password needed.',
    };
  }
  return {
    title: `Sign in to ${brandName}`,
    intro: 'Use the email you entered with. Your tickets and results are waiting.',
  };
}

export interface CodeCopy {
  title: string;
  action: string;
}

/**
 * The code step's title and button. A code to a phone is always a sign in:
 * the server only texts accounts that already have a number on file.
 */
export function codeStepCopy(mode: MyFitventAuthMode, channel: CodeChannel): CodeCopy {
  if (channel === 'sms') return { title: 'Check your phone', action: 'Sign in' };
  if (mode === 'signup') return { title: 'Confirm your email', action: 'Confirm email' };
  return { title: 'Check your email', action: 'Sign in' };
}

/** What follows "Didn't get it?" once a code has been sent again. */
export function resentNote(channel: CodeChannel): string {
  return channel === 'sms'
    ? 'Sent again. It can take a minute to arrive.'
    : "Sent again. Check spam if it's not there.";
}

export interface AccountLookup {
  exists: boolean;
}

/**
 * A quiet line under the email field once we know whether the address has an
 * account. Both modes reach the same endpoints, so this only tells the athlete
 * what will happen; it never blocks them.
 */
export function emailHint(mode: MyFitventAuthMode, lookup: AccountLookup | null | undefined): string | null {
  if (!lookup) return null;
  if (lookup.exists) {
    if (mode === 'signup') return "You already have an account. Continue and we'll sign you in.";
    // No name: the lookup runs before any code is checked, so it never says
    // who the address belongs to.
    return null;
  }
  if (mode === 'signin') return "There's no account with this email yet. Continue and we'll make one.";
  return null;
}

/**
 * The participant auth API and its client hook return developer-flavoured
 * strings ("Invalid or expired code", "Internal server error"). Turn the ones
 * we know into plain words, and never show a raw server message we don't.
 */
export function plainAuthError(raw: string | null | undefined, context: AuthErrorContext): string | null {
  const message = (raw ?? '').trim();
  if (!message) return null;
  const m = message.toLowerCase();

  if (m.includes('too many')) {
    return context === 'code'
      ? 'Too many tries. Send yourself a new code and use that one.'
      : 'Too many tries for now. Wait a few minutes and try again.';
  }
  if (m.includes('connection')) return "We couldn't reach Fitvent. Check your connection and try again.";
  if (m.includes('invalid or expired') || m === 'invalid code') {
    return "That code didn't work. Check it, or send a new one. Codes last 10 minutes.";
  }
  if (m.includes('failed to send') || m.includes('failed to resend')) {
    return "We couldn't send a code just now. Try again in a moment.";
  }
  if (m.includes('verify your email')) {
    return 'Sign up with your email address first. You can add a phone number after.';
  }
  if (m.includes('account not found')) return "We couldn't find that account. Try your email address instead.";
  if (context === 'phone' && (m.includes('invalid request') || m.includes('failed to save phone'))) {
    return "That number doesn't look right. Check it, or skip this for now.";
  }
  return 'Something went wrong on our side. Try again in a moment.';
}

/**
 * The digits the server left visible in a masked phone ("••••••789"), so the
 * code step can say "the number ending 789". Null when there are none.
 */
export function phoneEnding(masked: string | null | undefined): string | null {
  const digits = (masked ?? '').replace(/\D/g, '');
  return digits ? digits.slice(-3) : null;
}
